import { FormEvent, useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next/pages'
import { EditInstalmentPopupProps } from './types'
import styles from './EditInstalmentDrawer.module.scss'
import { InputGroupKeys, SimulationFieldKeys } from 'store/customer/enums'
import { getTotalSimulatedPrice } from 'utils/price'
import { formatAmount } from 'utils/format'
import { updateInput } from 'store/customer/slice'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { Button, Drawer, Form, TextField } from '@boltenergy-be/design-system'

const EditInstalmentDrawer = ({ isOpen, onRequestClose }: EditInstalmentPopupProps) => {
  // Redux store
  const { isLoading } = useStoreSelector((store) => store.customer.flows.simulation)
  const { simulation } = useStoreSelector((store) => store.customer.inputs)
  const { partnerID } = useStoreSelector((store) => store.marketing)
  const dispatch = useStoreDispatch()

  // I18n
  const { t } = useTranslation('editInstalmentPopup')

  // Constants
  const simulatedPrice = getTotalSimulatedPrice()
  const personalInstalment = simulation[SimulationFieldKeys.FORCED_AMOUNT] || simulatedPrice
  const maxInstalment = 10_000

  // State
  const [instalment, setInstalment] = useState<number>(personalInstalment)
  const [tooHigh, setTooHigh] = useState<boolean>(false)
  const [tooLow, setTooLow] = useState<boolean>(false)
  const error = tooHigh
    ? t('maximumInstalmentError', 'Je voorschot is te hoog')
    : tooLow
      ? t('minimumInstalmentError', 'Je voorschot moet minstens € {{instalment}} zijn.', {
          instalment: formatAmount(partnerID ? 20 : simulatedPrice)
        })
      : undefined

  // Update local state when instalment changes
  useEffect(() => {
    setInstalment(personalInstalment)
  }, [personalInstalment, simulatedPrice])

  /**
   * Submits the new instalment and updates the simulation
   */
  const submitInstalment = async (e: FormEvent) => {
    e.preventDefault()
    if (!simulatedPrice) return

    setTooLow(false)
    setTooHigh(false)

    if ((!partnerID && (!instalment || instalment < simulatedPrice)) || (partnerID && instalment < 20)) return setTooLow(true)
    if (instalment > maxInstalment) return setTooHigh(true)

    // Update the instalment
    dispatch(updateInput({ group: InputGroupKeys.SIMULATION, key: SimulationFieldKeys.FORCED_AMOUNT, value: instalment }))

    // Close the popup
    onRequestClose()
  }

  return (
    <Drawer title={t('editInstalment.title', 'Wijzig voorschot')} titleVariant="h3" {...{ isOpen, onRequestClose }}>
      <p>{t('editInstalment.description', { min: formatAmount(simulatedPrice) })}</p>

      <Form className={styles.form} onSubmit={(e) => submitInstalment(e)}>
        <TextField
          type="number"
          inputMode="numeric"
          id="electricity-usage-total"
          name="electricity-usage-total"
          value={instalment}
          onChange={(event) => setInstalment(event.target.value ? Number(event.target.value) : undefined)}
          prefix={{ as: 'euro' }}
          label={t('dayMeter', 'Maandelijks voorschot (incl. btw)')}
          error={error}
          grid="full"
        />

        <Form.Footer className="flex-col gap-300 mt-auto">
          <Button loading={isLoading} isFullwidth>
            {t('submit', 'Wijzig voorschot')}
          </Button>

          <Button
            variant="secondary"
            loading={isLoading}
            isFullwidth
            onClick={() => {
              dispatch(updateInput({ group: InputGroupKeys.SIMULATION, key: SimulationFieldKeys.FORCED_AMOUNT, value: null }))
              setInstalment(personalInstalment)
              onRequestClose()
            }}
          >
            {t('reset', 'Behoud je persoonlijk voorstel')}
          </Button>
        </Form.Footer>
      </Form>
    </Drawer>
  )
}

export default EditInstalmentDrawer
